import { useState, memo } from "react";
import { Link } from "react-router-dom";
import { Share2 } from "lucide-react";

const formatPrice = (price) => {
  return new Intl.NumberFormat("ro-RO").format(price);
};

const formatKm = (km) => {
  return new Intl.NumberFormat("ro-RO").format(km);
};

const getFuelLabel = (fuel) => {
  const labels = { benzina: "Benzină", diesel: "Diesel", hibrid: "Hibrid", electric: "Electric" };
  return labels[fuel] || fuel;
};

export const CarCard = memo(function CarCard({ car }) {
  const [copied, setCopied] = useState(false);
  const hasDiscount = car.old_price && car.old_price > car.price;
  
  const defaultImage = "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=600&h=400&fit=crop";
  const carDetailUrl = `/masini/${car.id}`;
  const fullUrl = `${window.location.origin}${carDetailUrl}`;

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({ title: car.title, url: fullUrl });
      } catch {}
    } else {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div 
      className="car-card h-full flex flex-col"
      data-testid={`car-card-${car.id}`}
    >
      {/* Image Container */}
      <Link to={carDetailUrl} className="block relative aspect-[4/3] overflow-hidden bg-[#111111]">
        <img
          src={car.images?.[0] || defaultImage}
          alt={car.title}
          className="car-image w-full h-full object-cover transition-opacity duration-500"
          loading="lazy"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        
        {/* Share button overlay */}
        <button
          onClick={handleShare}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-black/70 transition-all duration-200 z-10"
          data-testid={`share-btn-${car.id}`}
          aria-label="Distribuie anunțul"
        >
          <Share2 className="w-4 h-4" />
        </button>

        {/* Copied toast */}
        {copied && (
          <div className="absolute top-14 right-3 px-3 py-1.5 rounded-lg bg-white text-black text-xs font-medium z-20 animate-fade-in">
            Link copiat!
          </div>
        )}

        {/* Badge */}
        {car.badge && (
          <span
            data-testid={`badge-${car.id}`}
            className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider z-10 ${
              car.badge === 'reducere'
                ? 'bg-red-500/90 text-white'
                : car.badge === 'nou'
                ? 'bg-white/90 text-black'
                : 'bg-amber-500/90 text-black'
            }`}
          >
            {car.badge}
          </span>
        )}

        {/* Subtle overlay on hover */}
        <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </Link>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        {/* Title */}
        <Link 
          to={carDetailUrl}
          className="font-bold text-lg text-white mb-3 hover:text-gray-300 transition-colors block"
        >
          {car.title}
        </Link>

        {/* Specs - Simple inline */}
        <div className="text-sm text-gray-400 mb-4">
          {car.year} • {getFuelLabel(car.fuel)} • {formatKm(car.kilometers)} km
        </div>

        {/* Price and Share */}
        <div className="mt-auto flex items-end justify-between gap-3">
          <div>
            {hasDiscount && (
              <span className="price-old mr-2">{formatPrice(car.old_price)} €</span>
            )}
            <span className="text-2xl font-bold text-white">{formatPrice(car.price)} €</span>
          </div>
          
          {/* Mobile share button */}
          <button
            onClick={handleShare}
            className="w-10 h-10 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center text-gray-400 hover:text-white hover:border-[#3a3a3a] transition-all duration-200 lg:hidden"
            data-testid={`share-mobile-btn-${car.id}`}
            aria-label="Distribuie anunțul"
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fade-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
      `}</style>
    </div>
  );
});
